// src/store/thunks/connection/disconnectPeer.ts

import { createAppAsyncThunk } from '../createAppAsyncThunk';

interface DisconnectPeerParams {
  sixId: string;
  peerId: string;
}

/**
 * Disconnect from a specific peer in a Six's P2P network.
 * Closes all active connections to the specified peer.
 */
export const disconnectPeer = createAppAsyncThunk(
  'connection/disconnectPeer',
  async(
    { sixId, peerId }: DisconnectPeerParams,
    { extra: { pocServices, loggerService }, rejectWithValue }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:connection' });
    const { p2pStackManagerService } = pocServices;

    try {
      const libp2pNode = p2pStackManagerService.getLibp2pNode(sixId);

      if (libp2pNode === null) {
        throw new Error(`No LibP2P node found for six '${sixId}'`);
      }

      // Get all connections to this peer
      const connections = libp2pNode.getConnections();
      const peerConnections = connections.filter(
        conn => conn.remotePeer.toString() === peerId
      );

      if (peerConnections.length === 0) {
        log.warn(`No connections found to peer ${peerId.substring(0, 20)}...`);
        return { sixId, peerId, disconnectedCount: 0 };
      }

      // Close all connections to this peer
      await Promise.all(
        peerConnections.map(async conn => { await conn.close(); })
      );

      log.info(
        `Disconnected from peer ${peerId.substring(0, 20)}... (closed ${peerConnections.length} connection(s))`
      );

      return {
        sixId,
        peerId,
        disconnectedCount: peerConnections.length,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error(`Failed to disconnect from peer ${peerId}:`, errorMessage);
      return rejectWithValue({ sixId, peerId, message: errorMessage });
    }
  }
);
